import type { EvidenceType } from '@et/schema';
import { useTranslation } from 'react-i18next';

/** Convencao de proveniencia: cheia = primaria, contorno = externa, tracejada = indirecta. */
export type Provenance = 'primary' | 'external' | 'indirect';

export interface EvidenceGlyphProps {
  readonly type: EvidenceType;
  readonly provenance?: Provenance;
  readonly size?: number;
  readonly decorative?: boolean;
  readonly className?: string;
}

const SHAPES: Record<EvidenceType, string> = {
  archaeology: 'M3.5 3.5 h11 v11 h-11 Z',
  manuscript: 'M4 2.5 h5.5 L14 7 v8.5 H4 Z',
  epigraphy: 'M9 2 l6 3.4 v7.2 L9 16 l-6-3.4 V5.4 Z',
  numismatics: 'M9 2.5 a6.5 6.5 0 1 0 0.01 0 Z',
  primary_literary: 'M9 2 l7 7 -7 7 -7 -7 Z',
  external_literary: 'M9 2.5 l6.5 6.5 -6.5 6.5 -6.5 -6.5 Z',
  historical_event: 'M9 2.5 l6.5 12 h-13 Z',
  medical: 'M7.8 3 h2.4 v4.8 H15 v2.4 h-4.8 V15 H7.8 v-4.8 H3 V7.8 h4.8 Z',
  textual_criticism: 'M9 2.5 a6.5 6.5 0 1 0 0.01 0 Z',
  linguistic: 'M9 2.5 l5.6 3.25 v6.5 L9 15.5 l-5.6 -3.25 v-6.5 Z',
  legal_administrative: 'M3.5 2.5 h8 L14.5 5.5 v10 h-11 Z',
  material_culture: 'M4 4 h10 v7 a5 5 0 0 1 -10 0 Z',
};

/**
 * Detalhes que impedem duas formas de colapsarem uma na outra.
 *
 * Nem sempre e um caminho identico: `epigraphy` e `linguistic` sao ambos
 * hexagonos e so diferem no raio, o que ninguem distingue de relance. Um teste
 * que compare markup nao apanha isso — apanha-o quem olha.
 */
const DETAIL: Partial<Record<EvidenceType, React.ReactNode>> = {
  manuscript: <path d="M9.5 2.5 V7 H14" fill="none" stroke="currentColor" strokeWidth={1.4} />,
  numismatics: <circle cx={9} cy={9} r={2.4} fill="none" stroke="currentColor" strokeWidth={1.6} />,
  textual_criticism: <path d="M4.6 13.4 L13.4 4.6" stroke="currentColor" strokeWidth={2} />,
  linguistic: <path d="M5.2 9 H12.8" stroke="currentColor" strokeWidth={1.6} />,
  legal_administrative: <path d="M11.5 2.5 V5.5 H14.5" fill="none" stroke="currentColor" strokeWidth={1.4} />,
};

/**
 * §22 — a forma diz o tipo. Isto e o que permite ler a timeline em
 * monocromatico: retirar a cor nao retira informacao.
 */
export function EvidenceGlyph({
  type, provenance = 'primary', size = 18, decorative = false, className,
}: EvidenceGlyphProps) {
  const { t } = useTranslation('lexicon');
  const label = t(`evidenceType.${type}`);
  const filled = provenance === 'primary';

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 18 18"
      data-evidence-type={type}
      data-provenance={provenance}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : label}
    >
      {!decorative && <title>{label}</title>}
      <path
        d={SHAPES[type]}
        fill={filled ? 'currentColor' : 'none'}
        stroke={filled ? 'none' : 'currentColor'}
        strokeWidth={filled ? undefined : 2}
        strokeLinejoin="round"
        strokeDasharray={provenance === 'indirect' ? '3 2.4' : undefined}
      />
      {DETAIL[type]}
    </svg>
  );
}
